'use client';
import { resolveImageAlt } from '@eliadmin/core/src/utils/image-alt-utils';


import Link from '@eliadmin/core/src/components/link';
import Image from '@eliadmin/core/src/components/image/image-wrapped';

import type { Service } from '../../types/services';

import { cn } from '@eliadmin/core/src/utils/style-utils';
import { Button } from '../ui/button';

import { GoArrowUpRight } from 'react-icons/go';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { HtmlRendererRaw } from '@eliadmin/core/src/components/ui/html-renderer-raw';

export function ServiceCard({
  service,
  criticalImage = false,
}: {
  service: Service;
  criticalImage?: boolean;
}) {
  const { t } = useTranslations();

  return (
    <div
      className={cn(
        'relative col-span-1 flex w-full flex-col items-start justify-between gap-16 p-6 sm:h-[300px] md:h-[360px] md:p-8 lg:h-[440px]',
        'group overflow-hidden',
        'ea-service-card',
      )}
    >
      <h2 className="relative z-10 text-xl font-bold tracking-tighter text-white md:text-2xl lg:text-3xl xl:text-[38px]">
        {service.name}
      </h2>
      <div
        className={cn(
          'flex w-full translate-y-0 flex-col items-start justify-start gap-4 sm:translate-y-[300px] lg:flex-row lg:items-end lg:justify-between',
          'relative z-10 transition-all sm:group-hover:translate-y-0',
        )}
      >
        <HtmlRendererRaw
          className={cn('w-full text-white', 'ea-service-card__description')}
          html={service.short_description}
        />
        <Button
          asChild
          variant={'primary-foreground'}
          className="max-h-11 whitespace-nowrap sm:max-h-[50px] lg:max-h-[54px]"
        >
          <Link prefetch={false} href={service.slug}>
            {t('text792')} <GoArrowUpRight className="h-5 w-5 sm:h-6 sm:w-6" />
          </Link>
        </Button>
      </div>
      <div
        className={cn('absolute inset-0 z-[1] h-full w-full', 'ea-service-slide__image-overlay')}
      />
      <Image
        src={service?.image?.url ?? '/images/placeholder.png'}
        alt={resolveImageAlt(service?.image, service?.name)}
        width={0}
        height={0}
        className="absolute inset-0 z-0 h-full w-full object-cover object-center"
        sizes="(min-width: 1232px) 588px, (min-width: 640px) 50vw, 100vw"
        priority={criticalImage}
        loading={criticalImage ? 'eager' : 'lazy'}
        fetchPriority={criticalImage ? 'high' : 'auto'}
      />
    </div>
  );
}
